/**
 * 008-reporting-dashboards T008/T009/T012 — `005`'s tile, wired for real (there was no tile here
 * before this feature). `varianceRate` is a fraction the API already computed (events over lines
 * packed, `null` when nothing was packed); this only `Intl.NumberFormat`-percent-formats it, it
 * does not compute a rate (constitution I, FR-002/DA-10 — `null` is rendered as "no packing in
 * this period", never coerced to `0%`).
 *
 * Drills through to `/reports/variance` (T015, now built), same `GET /api/warehouse/reports/variance`
 * this tile itself reads. The tile, its link and the report route are all gated on the one code that route
 * requires (`packing.oversight`), so `showReportLink` is passed by dashboard.tsx from that same check. Drawn by `StatusCard`.
 */
import { PiGaugeDuotone as Gauge } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { StatusCard, UnreadableStatusCard } from "./StatusCard";
import type { VarianceReportView } from "../../lib/services/warehouse";

const PERCENT = new Intl.NumberFormat("en-IN", { style: "percent", maximumFractionDigits: 1 });
const icon = <Gauge aria-hidden className="size-5" />;

function unreadable(error: unknown) {
  return <UnreadableStatusCard title="Packing variance rate" icon={icon} error={error} fallback="Could not be read — this is not the same as zero variance." />;
}

export function PackingVarianceTile({ resolve, showReportLink }: { resolve: Promise<VarianceReportView>; showReportLink: boolean }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable}>
      {(report) => {
        const rate = report.varianceRate === null ? null : Number(report.varianceRate);
        return (
          <StatusCard
            title="Packing variance rate"
            icon={icon}
            tone={rate === null ? "muted" : rate > 0 ? "warning" : "success"}
            value={rate === null ? "No packing yet" : PERCENT.format(rate)}
            word={rate === null}
            caption="Amber and red events over lines packed, trailing 30 days."
            link={showReportLink ? { to: "/reports/variance", label: "Open the variance report" } : undefined}
          />
        );
      }}
    </TileBoundary>
  );
}
